import { create } from 'zustand'
import { getMeta, setMeta } from '../lib/db'
import { haversineMeters, type LngLatEle } from '../lib/geo'
import { friendlyGeoError, keepScreenOn } from '../lib/platform'
import type { Sport } from '../lib/models'

export type RecorderStatus = 'idle' | 'recording' | 'paused'

interface RecorderState {
  status: RecorderStatus
  sport: Sport
  points: LngLatEle[]
  times: number[]
  gaps: number[]
  distanceM: number
  ascentM: number
  startedAt: number | null
  /** seconds actually moving (speed > 0.7 m/s between fixes) */
  movingTimeS: number
  lastFix: GeolocationPosition | null
  gpsError: string | null
  start: (sport: Sport) => void
  pause: () => void
  resume: () => void
  /** stops and returns the captured track (caller saves it as a tour) */
  stop: () => { points: LngLatEle[]; times: number[]; gaps: number[]; distanceM: number; ascentM: number; startedAt: number; movingTimeS: number } | null
  discard: () => void
  restore: () => Promise<void>
}

const CHECKPOINT_KEY = 'recording.checkpoint'
let watchId: number | null = null
let lastEleRef: number | null = null

function stopWatch(): void {
  if (watchId != null) navigator.geolocation.clearWatch(watchId)
  watchId = null
}

export const useRecorder = create<RecorderState>((set, get) => {
  const onFix = (pos: GeolocationPosition) => {
    const s = get()
    if (s.status !== 'recording') return
    if (pos.coords.accuracy > 50) return // ignore junk fixes
    const p: LngLatEle = [pos.coords.longitude, pos.coords.latitude, pos.coords.altitude ?? 0]
    const prev = s.points[s.points.length - 1]
    const prevT = s.times[s.times.length - 1]
    const update: Partial<RecorderState> = { lastFix: pos, gpsError: null }

    if (prev) {
      const d = haversineMeters(prev, p)
      const dt = (pos.timestamp - prevT) / 1000
      if (d < 2 && dt < 10) return // stationary noise — skip
      // long silence (locked screen / app switch) → mark a gap, don't draw a line
      if (dt > 60) update.gaps = [...s.gaps, s.points.length - 1]
      else {
        update.distanceM = s.distanceM + d
        if (dt > 0 && d / dt > 0.7) update.movingTimeS = s.movingTimeS + dt
      }
      const ele = p[2]
      if (ele && lastEleRef != null && ele - lastEleRef > 3) {
        update.ascentM = s.ascentM + (ele - lastEleRef)
        lastEleRef = ele
      } else if (ele && lastEleRef != null && lastEleRef - ele > 3) {
        lastEleRef = ele
      }
    } else if (p[2]) lastEleRef = p[2]

    update.points = [...s.points, p]
    update.times = [...s.times, pos.timestamp]
    set(update)
    // checkpoint so a crash/reload loses nothing
    if (update.points.length % 5 === 0) {
      void setMeta(
        CHECKPOINT_KEY,
        JSON.stringify({
          points: update.points,
          times: update.times,
          gaps: update.gaps ?? s.gaps,
          distanceM: update.distanceM ?? s.distanceM,
          ascentM: update.ascentM ?? s.ascentM,
          startedAt: s.startedAt,
          movingTimeS: update.movingTimeS ?? s.movingTimeS,
        }),
      )
    }
  }

  const startWatch = () => {
    stopWatch()
    watchId = navigator.geolocation.watchPosition(onFix, (e) => set({ gpsError: friendlyGeoError(e.code) }), {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 30000,
    })
  }

  // iOS suspends pages: after resume, restart the watch so fixes flow again
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && get().status === 'recording') startWatch()
  })

  return {
    status: 'idle',
    sport: 'hike',
    points: [],
    times: [],
    gaps: [],
    distanceM: 0,
    ascentM: 0,
    startedAt: null,
    movingTimeS: 0,
    lastFix: null,
    gpsError: null,

    start: (sport) => {
      lastEleRef = null
      set({
        status: 'recording',
        sport,
        points: [],
        times: [],
        gaps: [],
        distanceM: 0,
        ascentM: 0,
        movingTimeS: 0,
        startedAt: Date.now(),
        gpsError: null,
      })
      keepScreenOn(true)
      startWatch()
    },

    pause: () => {
      stopWatch()
      keepScreenOn(false)
      set({ status: 'paused' })
    },

    resume: () => {
      const s = get()
      set({ status: 'recording', gaps: s.points.length ? [...s.gaps, s.points.length - 1] : s.gaps })
      keepScreenOn(true)
      startWatch()
    },

    stop: () => {
      const s = get()
      if (!s.startedAt || s.points.length < 2) {
        get().discard()
        return null
      }
      stopWatch()
      keepScreenOn(false)
      const result = {
        points: s.points,
        times: s.times,
        gaps: s.gaps,
        distanceM: s.distanceM,
        ascentM: s.ascentM,
        startedAt: s.startedAt,
        movingTimeS: s.movingTimeS,
      }
      set({ status: 'idle', points: [], times: [], gaps: [], distanceM: 0, ascentM: 0, startedAt: null, movingTimeS: 0 })
      void setMeta(CHECKPOINT_KEY, '')
      return result
    },

    discard: () => {
      stopWatch()
      keepScreenOn(false)
      set({ status: 'idle', points: [], times: [], gaps: [], distanceM: 0, ascentM: 0, startedAt: null, movingTimeS: 0 })
      void setMeta(CHECKPOINT_KEY, '')
    },

    restore: async () => {
      const raw = await getMeta(CHECKPOINT_KEY)
      if (typeof raw !== 'string' || !raw) return
      try {
        const cp = JSON.parse(raw) as {
          points: LngLatEle[]
          times: number[]
          gaps: number[]
          distanceM: number
          ascentM: number
          startedAt: number
          movingTimeS: number
        }
        if (cp.points?.length >= 2) {
          set({ ...cp, status: 'paused', lastFix: null, gpsError: null })
          lastEleRef = cp.points[cp.points.length - 1][2] || null
        }
      } catch {
        // corrupt checkpoint — ignore
      }
    },
  }
})
